import { Link } from 'react-router-dom'
import { Shield, Twitter, ExternalLink } from 'lucide-react'

const FOOTER_COLS = [
  {
    heading: 'Product',
    links: [
      { label: 'Features', path: '/features' },
      { label: 'Pricing', path: '/pricing' },
      { label: 'Documentation', path: '/docs' },
      { label: 'Blog', path: '/blog' },
      { label: 'Free Trial', path: '/free-trial' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About', path: '/about' },
      { label: 'Contact', path: '/contact' },
      { label: 'FAQ', path: '/faq' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Privacy Policy', path: '/privacy' },
      { label: 'Terms of Service', path: '/terms' },
    ],
  },
]

const SOCIAL = [
  { icon: Twitter, label: 'Twitter', href: 'https://twitter.com/mevshield' },
]

export function SiteFooter() {
  return (
    <footer className="border-t border-white/[0.06] bg-white/[0.015]">
      <div className="max-w-7xl mx-auto px-4 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
        {/* Brand column — spans 2 cols on lg */}
        <div className="lg:col-span-2 flex flex-col gap-5">
          <Link to="/" className="flex items-center gap-2.5 w-fit">
            <div className="w-8 h-8 rounded-lg bg-gradient-brand flex items-center justify-center shadow-glow-sky shrink-0">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold text-lg tracking-wide text-white">
              MEV<span className="text-primary">Shield</span>
            </span>
          </Link>

          <p className="text-sm text-white/45 leading-relaxed max-w-xs">
            Enterprise-grade MEV protection for DeFi protocols, institutional traders, and
            blockchain builders.
          </p>

          {/* Social links */}
          <div className="flex items-center gap-2">
            {SOCIAL.map(({ icon: Icon, label, href }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="w-8 h-8 rounded-lg border border-white/10 bg-white/[0.04] flex items-center justify-center text-white/40 hover:text-white hover:border-white/20 hover:bg-white/[0.08] transition-all"
              >
                <Icon className="w-3.5 h-3.5" />
              </a>
            ))}
          </div>

          {/* Status badge */}
          <a
            href="https://mevshield.ai/health"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 w-fit px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] transition-all group"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs text-white/50 group-hover:text-white/70 transition-colors">
              All systems operational
            </span>
            <ExternalLink className="w-2.5 h-2.5 text-white/30" />
          </a>
        </div>

        {/* Link columns */}
        {FOOTER_COLS.map(({ heading, links }) => (
          <div key={heading} className="flex flex-col gap-4">
            <h4 className="text-xs font-semibold text-white/30 uppercase tracking-widest">
              {heading}
            </h4>
            <ul className="flex flex-col gap-2.5">
              {links.map(({ label, path }) => (
                <li key={path}>
                  <Link
                    to={path}
                    className="text-sm text-white/50 hover:text-white transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/25">
            © 2026 MEV Shield — Aurigraph DLT Corp. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="text-xs text-white/25 hover:text-white/50 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-xs text-white/25 hover:text-white/50 transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
